import { useStyles } from "../../../shared/styles/useStyles";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducers/rootReducer";
import {
  AnswerTimeCorrespondence,
  ICommunicationType,
  ICorrespondence,
} from "../../../interfaces/Correspondence";
import { FC, useEffect, useState } from "react";
import moment, {Moment} from "moment-business-days";
import {WorkScheduleFormValues} from "../../../module/superAdmin/form/WorkScheduleFormValues";
import {SpecialDay} from "../../../module/superAdmin/form/SpecialDay";

interface Props {
  paramResponse?: ICommunicationType | null;
  answer?: AnswerTimeCorrespondence | null;
  correspondence: ICorrespondence;
}

/**
 * Extrae los datos necesarios para calcular el semáforo de una correspondencia,
 * incluyendo días disponibles, tipo de días, fecha de radicación, y en caso de corresponder,
 * configuración personalizada de días festivos y horarios laborales.
 *
 * Soporta retrocompatibilidad: si no hay configuración personalizada, se usa la lógica clásica.
 *
 * @param correspondence - Objeto de correspondencia con información clave.
 * @param paramResponse - Tipo de comunicación seleccionado (ej. Petición, Queja...).
 * @param paramResponseTime - Configuración global de tiempos de respuesta por tipo.
 * @param paramsProcedure - Configuración adicional por procedimiento.
 * @returns Objeto con todos los datos necesarios para calcular el semáforo.
 */
const getDaysAndTypeDays = (
  correspondence: ICorrespondence,
  paramResponse: ICommunicationType | null,
  paramResponseTime: any,
  paramsProcedure: any
) => {
  const documentDate = correspondence.createAt.toDate();

  const procedureFilter = paramsProcedure.filter(
    (item: any) =>
      item.procedureName === correspondence.communicationType?.option
  );

  const paramNameDays = paramResponse?.option + "Days";
  const paramNameTypeDays = paramResponse?.option + "TypeDays";

  const extensionDays = correspondence.finalExtensionDate
    ? moment(correspondence.finalExtensionDate.toDate()).diff(documentDate, "days")
    : 0;

  let daysAvailableDocument =
    correspondence.responseTimeDays || paramResponseTime[paramNameDays];

  daysAvailableDocument += extensionDays;

  let typeDays = procedureFilter.length
    ? procedureFilter[0].typeDays
    : paramResponseTime[paramNameTypeDays];

  typeDays =
    ["Guardianship", "IncidentOfContempt", "CommunicationsControlEntities"].includes(
      correspondence.communicationType?.option || ""
    )
      ? "BUSINESSDAYS"
      : typeDays;


  const specialDay = correspondence.specialDay ?? [];
  const workSchedule = correspondence.workScheduleFormValues ?? null;

  const isCustomSchedule =
    Array.isArray(specialDay) &&
    specialDay.length > 0 &&
    workSchedule !== null &&
    (Array.isArray(workSchedule.days) || Array.isArray((workSchedule as any).schedule)) &&
    ((workSchedule.days ?? (workSchedule as any).schedule)?.length === 7)

  return {
    daysAvailableDocument,
    typeDays,
    documentDate,
    specialDay,
    workSchedule,
    isCustomSchedule,
  };
};

/**
 * Calcula el porcentaje de días transcurridos basado en el tipo de días y el tiempo disponible.
 *
 * @param typeDays - Tipo de días (días hábiles o calendario).
 * @param daysAvailableDocument - Total de días disponibles para la correspondencia.
 * @param createDate - Fecha de creación del documento.
 * @param answerDate - Fecha de respuesta de la correspondencia.
 * @param currentMoment - Momento actual.
 * @returns El porcentaje de días transcurridos.
 */
const calculatePercentage = (
  typeDays: string,
  daysAvailableDocument: number,
  createDate: Date,
  answerDate: Date,
  currentMoment: moment.Moment
) => {
  const startDate = moment(createDate).add(1, "day");

  const differenceBusinessDays = startDate.businessDiff(moment(answerDate));
  const differenceCalendarDays = Math.floor(
    moment(answerDate).diff(startDate, "days")
  );

  const percentageBusinessDays =
    (100 * differenceBusinessDays) / daysAvailableDocument;
  const percentageCalendarDays =
    (100 * differenceCalendarDays) / daysAvailableDocument;

  return typeDays === "BUSINESSDAYS"
    ? percentageBusinessDays
    : percentageCalendarDays;
};
/**
 * Calcula el porcentaje de días transcurridos para una correspondencia, considerando:
 * - Tipo de días (hábiles o calendario)
 * - Configuración de días festivos y horarios laborales personalizados (si existen)
 * - Retrocompatibilidad con correspondencias antiguas sin dichos campos
 *
 * @param correspondence - Objeto de correspondencia
 * @param paramResponse - Tipo de comunicación (ej. Petición, Queja, etc.)
 * @param paramResponseTime - Configuración global de tiempos por tipo
 * @param paramsProcedure - Parámetros asociados al procedimiento
 * @returns Objeto con porcentaje de vencimiento y tipo de días usados
 */
export const calculateTrafficLightPercentage = (
  correspondence: ICorrespondence,
  paramResponse: ICommunicationType | null,
  paramResponseTime: any,
  paramsProcedure: any
): { percentage: number | null; typeDays: string | null } => {
  const {
    daysAvailableDocument,
    typeDays,
    documentDate,
    specialDay,
    workSchedule,
    isCustomSchedule,
  } = getDaysAndTypeDays(
    correspondence,
    paramResponse,
    paramResponseTime,
    paramsProcedure
  );

  if (!daysAvailableDocument || !typeDays) {
    return { percentage: null, typeDays: null };
  }

  const currentMoment = moment();

  const answerDate = correspondence.answeredDate
    ? correspondence.answeredDate.toDate()
    : currentMoment.toDate();

  let percentage: number;

  if (isCustomSchedule && typeDays === "BUSINESSDAYS") {
    const normalizedWorkSchedule = {
      days:
        (workSchedule as any).days ??
        (workSchedule as any).schedule ??
        [],
      restrictOutsideWorkHours: workSchedule!.restrictOutsideWorkHours,
    };

    const diff = getCustomBusinessDaysDiff(
      documentDate,
      answerDate,
      normalizedWorkSchedule,
      specialDay
    );
    percentage = (100 * diff) / daysAvailableDocument;

  } else {
    percentage = calculatePercentage(
      typeDays,
      daysAvailableDocument,
      documentDate,
      answerDate,
      currentMoment
    );
  }

  return { percentage, typeDays };
};

/**
 * Componente que renderiza el semáforo de correspondencia basado en el porcentaje de días transcurridos.
 *
 * @param paramResponse - Información del tipo de comunicación seleccionada.
 * @param answer - Información sobre la respuesta dada a la correspondencia (fecha, etc).
 * @param correspondence - Objeto con los datos completos de la correspondencia.
 * @returns Un <div> que representa visualmente el color del semáforo según el porcentaje calculado.
 */
export const TrafficLight: FC<Props> = ({
                                          paramResponse,
                                          answer,
                                          correspondence,
                                        }) => {
  const classes = useStyles();

  const { paramResponseTime, paramsProcedure }: any = useSelector(
    (state: AppState) => state.correspondence
  );

  const [color, setColor] = useState("");

  useEffect(() => {
    if (correspondence.communicationType?.option === "Informativo") {
      setColor(""); // No pintar nada
      return;
    }

    const { percentage, typeDays } = calculateTrafficLightPercentage(
      correspondence,
      paramResponse!,
      paramResponseTime,
      paramsProcedure
    );

    if (percentage === null) {
      setColor(classes.circle_green);
      return;
    }

    switch (true) {
      case percentage < 60:
        setColor(classes.circle_green);
        break;
      case percentage >= 60 && percentage <= 70:
        setColor(classes.circle_yellow);
        break;
      case percentage > 70 && percentage <= 100:
        setColor(classes.circle_orange);
        break;
      case percentage > 100:
        setColor(classes.circle_red);
        break;
      default:
        setColor(classes.circle_green);
        break;
    }
  }, [
    paramResponse,
    paramResponseTime,
    paramsProcedure,
    correspondence,
    answer,
    classes.circle_green,
    classes.circle_yellow,
    classes.circle_orange,
    classes.circle_red,
  ]);

  if (!color) return null;

  return <div className={color}></div>;
};

/**
 * Calcula el número de días hábiles personalizados entre dos fechas,
 * excluyendo el día de inicio, los días festivos y los días no laborales
 * según la configuración del usuario.
 *
 * @param startDate - Fecha desde donde se empieza a contar (sin incluir).
 * @param endDate - Fecha hasta donde se cuenta (inclusive).
 * @param workSchedule - Configuración del horario laboral semanal.
 * @param specialDays - Lista de días festivos personalizados.
 * @returns Número de días hábiles entre las dos fechas dadas.
 */
function getCustomBusinessDaysDiff(
  startDate: Date,
  endDate: Date,
  workSchedule: WorkScheduleFormValues,
  specialDays: SpecialDay[]
): number {
  let count = 0;

  const holidays = new Set(specialDays.map((d) => moment(d.date).format("YYYY-MM-DD")));

  const rawStart = moment(startDate).startOf("day");
  const dayIndexStart = rawStart.isoWeekday() - 1;
  const workDayStart = workSchedule.days[dayIndexStart];
  const isStartHoliday = holidays.has(rawStart.format("YYYY-MM-DD"));
  const isStartWorkingDay = workDayStart?.isWorkingDay ?? false;

  const start = moment(startDate).add(1, "day").startOf("day");
  const end = moment(endDate).startOf("day");

  while (start.isSameOrBefore(end)) {
    const dayIndex = start.isoWeekday() - 1;
    const workDay = workSchedule.days[dayIndex];
    const isWorkingDay = workDay?.isWorkingDay ?? false;
    const isHoliday = holidays.has(start.format("YYYY-MM-DD"));

    if (isWorkingDay && !isHoliday) {
      count++;
    }

    start.add(1, "day");
  }

  if (isStartWorkingDay && isStartHoliday) {
    count = Math.max(0, count - 1);
  }

  return count;
}

export function calculateDeadlineDate(
  startDate: Date,
  daysToAdd: number,
  typeDays: string,
  scheduleData: WorkScheduleFormValues | null,
  specialDays: SpecialDay[]
): Moment {
  const start = moment(startDate).add(1, "day");

  if (typeDays === "CALENDAR") {
    return start.add(daysToAdd, "days");
  }

  if (scheduleData && scheduleData.days) {
    const workingWeekDays = scheduleData.days
      .map((day, index) => (day.isWorkingDay ? index : null))
      .filter((day): day is number => day !== null);

    const customHolidays = specialDays.map((d) =>
      moment(d.date).format("YYYY-MM-DD")
    );

    moment.updateLocale("custom", {
      workingWeekdays: workingWeekDays,
      holidays: customHolidays,
      holidayFormat: "YYYY-MM-DD",
    });

    return moment(start).locale("custom").businessAdd(daysToAdd, "days");
  }

  return moment(start).businessAdd(daysToAdd, "days");
}